import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Tooltip } from "recharts";
import { RefreshCw, TrendingUp, Clock, CheckCircle2, IndianRupee } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CardSkeleton } from "../components/Skeletons";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../contexts/AuthContext";

const CHART_COLORS = ["#534AB7", "#1D9E75", "#F59E0B", "#EF4444", "#8B5CF6"];

function KpiCard({ label, value, trend, color = "#534AB7", icon: Icon, testid, prefix = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.25 }}
      className="k-glass rounded-2xl p-5 relative overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-2 text-3xl font-bold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            {prefix}
            {typeof value === "number" ? value.toLocaleString() : value}
          </div>
        </div>
        <div className="p-2 rounded-xl" style={{ background: `${color}1A`, color }}>
          <Icon size={18} />
        </div>
      </div>
      <div className="h-14 mt-2 -mx-2 -mb-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trend || []}>
            <defs>
              <linearGradient id={`grad-${testid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="y" stroke={color} strokeWidth={2} fill={`url(#grad-${testid})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState("all");
  const [days, setDays] = useState("30");

  const load = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const [dash, depts] = await Promise.all([
        api.get("/dashboard", {
          params: { days, department_id: filterDept === "all" ? undefined : filterDept },
        }),
        departments.length ? Promise.resolve({ data: departments }) : api.get("/departments"),
      ]);
      setData(dash.data);
      if (!departments.length) setDepartments(depts.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDept, days]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "Outfit" }}
            data-testid="dashboard-title"
          >
            Good day, {user?.name?.split(" ")[0]}
          </motion.h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here is what's happening across your procurement pipeline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-44 k-glass" data-testid="filter-department">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 k-glass" data-testid="filter-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => load(true)} variant="outline" className="gap-2" data-testid="refresh-btn">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <KpiCard label="Pending Approvals" value={data.kpis.pending_approvals.value} trend={data.kpis.pending_approvals.trend} icon={Clock} color="#F59E0B" testid="kpi-pending" />
          <KpiCard label="Approved This Month" value={data.kpis.approved_this_month.value} trend={data.kpis.approved_this_month.trend} icon={CheckCircle2} color="#1D9E75" testid="kpi-approved" />
          <KpiCard label="Total PO Value" value={data.kpis.total_po_value.value} trend={data.kpis.total_po_value.trend} icon={IndianRupee} color="#534AB7" testid="kpi-po-value" prefix="₹" />
          <KpiCard label="Avg Lead Time (days)" value={data.kpis.avg_lead_time.value} trend={data.kpis.avg_lead_time.trend} icon={TrendingUp} color="#8B5CF6" testid="kpi-lead" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="k-glass rounded-2xl p-6 lg:col-span-1" data-testid="dept-donut">
          <h3 className="font-semibold mb-1">Department targets</h3>
          <p className="text-xs text-muted-foreground mb-4">Approved requests per department</p>
          {loading || !data ? (
            <div className="k-skeleton h-56 w-full" />
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.department_breakdown}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {data.department_breakdown.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "rgba(15, 14, 26, 0.96)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#F0EFF8", fontSize: 12, padding: "8px 12px", boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}
                    itemStyle={{ color: "#F0EFF8" }}
                    labelStyle={{ color: "#AFA9EC", fontWeight: 600, marginBottom: 4 }}
                    formatter={(value, name) => [`${value} requests`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {data && (
            <div className="grid grid-cols-2 gap-1.5 text-xs mt-2">
              {data.department_breakdown.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-muted-foreground truncate">{d.name}</span>
                  <span className="ml-auto font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="k-glass rounded-2xl p-6 lg:col-span-2" data-testid="recent-activity">
          <h3 className="font-semibold mb-1">Recent activity</h3>
          <p className="text-xs text-muted-foreground mb-4">Latest requests across your org</p>
          <div className="divide-y divide-border/60">
            {(data?.recent_activity || []).map((a) => (
              <div key={a.id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#534AB7]/15 text-[#534AB7] flex items-center justify-center text-xs font-semibold">
                  {(a.actor || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.actor}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.action}</div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
            {!loading && (data?.recent_activity || []).length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">No activity yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
